/**
 * /admin/whats-new — the updates coaches read on What's new. Staff write and
 * edit updates here, and publish or unpublish them. There is no delete.
 *
 * Aliases: admin whats new, changelog admin, publish update
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { Newspaper, Plus } from "lucide-react";
import { useMemo, useState } from "react";

import { AdminDataTable } from "~/components/admin/data-table";
import { WhatsNewEntryDialog } from "~/components/admin/whats-new-entry-dialog";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { ConfirmationDialog } from "~/components/ui/confirmation-dialog";
import { EmptyState } from "~/components/ui/empty-state";
import { PageHeader } from "~/components/ui/page-title";
import { requirePermission } from "~/lib/functions/admin-access";
import {
  adminWhatsNewEntriesQueryOptions,
  publishWhatsNewEntryFn,
  unpublishWhatsNewEntryFn,
} from "~/lib/functions/admin-whats-new";
import {
  listSearchSchema,
  listSortDefaults,
  type SortColumn,
  sortListRows,
} from "~/lib/shared/admin-list-sort";
import { FEATURE_MODULE_LABELS } from "~/lib/shared/features/modules";
import { hasPermission } from "~/lib/shared/permissions";
import { nextSortSearch } from "~/lib/shared/sort";
import { formatWhatsNewDate, type WhatsNewEntry } from "~/lib/shared/whats-new";

const SORTS = ["date", "title"] as const;
type WhatsNewSort = (typeof SORTS)[number];

const SORT_DEFAULTS = listSortDefaults<WhatsNewSort>("date", ["date"]);

const SORT_COLUMNS: Record<WhatsNewSort, SortColumn<WhatsNewEntry>> = {
  // A draft sorts by when it was written, so a fresh one is on top.
  date: { kind: "date", value: (row) => row.publishedAt ?? row.createdAt },
  title: { kind: "text", value: (row) => row.title },
};

export const Route = createFileRoute("/admin/whats-new/")({
  staticData: { title: "What's new" },
  validateSearch: listSearchSchema(SORTS),
  beforeLoad: ({ context }) =>
    requirePermission(context.adminPermissions, "whats-new:read"),
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(adminWhatsNewEntriesQueryOptions);
  },
  component: WhatsNewAdminPage,
});

function WhatsNewAdminPage(): React.ReactElement {
  const { adminPermissions } = Route.useRouteContext();
  const canWrite = hasPermission(adminPermissions, "whats-new:write");
  const { data } = useQuery(adminWhatsNewEntriesQueryOptions);
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();
  const sort = search.sort ?? SORT_DEFAULTS.sort;
  const direction = search.dir ?? SORT_DEFAULTS.direction(sort);

  // `null` writes a new update; undefined means the dialog is closed.
  const [editing, setEditing] = useState<WhatsNewEntry | null | undefined>();
  const [publishing, setPublishing] = useState<WhatsNewEntry | null>(null);

  const queryClient = useQueryClient();
  const refresh = (): Promise<void> =>
    queryClient.invalidateQueries({
      queryKey: adminWhatsNewEntriesQueryOptions.queryKey,
    });
  const publish = useMutation({
    mutationFn: (id: string) => publishWhatsNewEntryFn({ data: { id } }),
    onSuccess: refresh,
    onSettled: () => setPublishing(null),
  });
  const unpublish = useMutation({
    mutationFn: (id: string) => unpublishWhatsNewEntryFn({ data: { id } }),
    onSuccess: refresh,
  });

  const rows = useMemo(
    () =>
      sortListRows(
        data ?? [],
        sort,
        direction,
        SORT_COLUMNS,
        (row) => row.title,
      ),
    [data, sort, direction],
  );

  const columns: ColumnDef<WhatsNewEntry>[] = [
    {
      id: "title",
      header: "Update",
      cell: ({ row }) => (
        <div className="max-w-md space-y-1 whitespace-normal">
          <p className="font-medium text-gray-900">{row.original.title}</p>
          <p className="text-gray-500">{row.original.body}</p>
        </div>
      ),
    },
    {
      id: "modules",
      header: "Modules",
      cell: ({ row }) => (
        <span className="text-gray-500">
          {row.original.modules
            .map((module) => FEATURE_MODULE_LABELS[module])
            .join(", ")}
        </span>
      ),
    },
    {
      id: "link",
      header: "Link",
      cell: ({ row }) => (
        <span className="text-gray-500">{row.original.href ?? "—"}</span>
      ),
    },
    {
      id: "date",
      header: "Published",
      cell: ({ row }) =>
        row.original.publishedAt ? (
          <span className="text-gray-500">
            {formatWhatsNewDate(row.original.publishedAt)}
          </span>
        ) : (
          <Badge size="sm" variant="outline-secondary">
            Draft
          </Badge>
        ),
    },
    ...(canWrite
      ? [
          {
            id: "actions",
            header: () => <span className="sr-only">Actions</span>,
            meta: { align: "end" },
            cell: ({ row }) => (
              <div className="flex justify-end gap-2">
                <Button onClick={() => setEditing(row.original)} size="sm">
                  Edit
                </Button>
                {row.original.publishedAt ? (
                  <Button
                    disabled={unpublish.isPending}
                    onClick={() => unpublish.mutate(row.original.id)}
                    size="sm"
                  >
                    Unpublish
                  </Button>
                ) : (
                  <Button
                    onClick={() => setPublishing(row.original)}
                    size="sm"
                    variant="primary"
                  >
                    Publish
                  </Button>
                )}
              </div>
            ),
          } satisfies ColumnDef<WhatsNewEntry>,
        ]
      : []),
  ];

  const changeSort = (column: WhatsNewSort): void => {
    navigate({
      search: (previous) => ({
        ...previous,
        ...nextSortSearch({ sort, direction }, column, SORT_DEFAULTS),
      }),
    });
  };

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        actions={
          canWrite && (
            <Button
              className="gap-2"
              onClick={() => setEditing(null)}
              variant="primary"
            >
              <Plus aria-hidden="true" className="size-4" />
              New update
            </Button>
          )
        }
        description="What coaches read on What's new. A draft shows to nobody until it is published."
        title="What's new"
      />

      {(publish.error ?? unpublish.error) && (
        <p className="text-sm text-destructive" role="alert">
          {(publish.error ?? unpublish.error)?.message}
        </p>
      )}

      <AdminDataTable
        columns={columns}
        data={rows}
        direction={direction}
        emptyState={
          <EmptyState
            description="Write the first update, then publish it for coaches."
            icon={Newspaper}
            size="sm"
            title="No updates yet"
          />
        }
        getRowId={(row) => row.id}
        onSortChange={changeSort}
        sort={sort}
        sorts={SORTS}
      />

      {editing !== undefined && (
        <WhatsNewEntryDialog
          entry={editing}
          onClose={() => setEditing(undefined)}
        />
      )}

      <ConfirmationDialog
        confirmText="Publish"
        description={`"${publishing?.title ?? ""}" shows to every coach on their next page load.`}
        isLoading={publish.isPending}
        onConfirm={() => {
          if (publishing) publish.mutate(publishing.id);
        }}
        onOpenChange={(open) => {
          if (!open) setPublishing(null);
        }}
        open={publishing !== null}
        title="Publish this update?"
      />
    </div>
  );
}
